import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  V2Alert,
  V2Badge,
  V2Button,
  V2FormField,
  V2FormGrid,
  V2Modal,
} from '@sakhtar/design-system/react';
import { ApiError, apiDelete, apiPost, apiPut } from '../../lib/apiClient.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { faDigits } from '../../lib/faDigits.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  CommissionMethod,
  CommissionPlan,
  PeriodMetrics,
  ReleasePolicy,
  TeamMember,
} from '../../lib/types/commission.js';
import {
  METHOD_LABELS,
  RELEASE_LABELS,
  displayToRial,
  rialToDisplay,
} from './teamPerformanceModel.js';

const METHOD_HINTS: Record<CommissionMethod, string> = {
  product_rate:
    'پورسانت طبق درصدی که برای هر کالا در تب «نرخ پورسانت محصولات» تعریف شده؛ برای فروشندگانی که طرح اختصاصی ندارند همین روش اعمال می‌شود.',
  percent_of_sale: 'یک درصد ثابت از مبلغ کل فاکتور (بدون مالیات)، مثلاً ۲٫۵٪ از هر فروش.',
  fixed_per_invoice: 'مبلغ مشخصی برای هر فاکتور قطعی‌شده، فارغ از اندازهٔ فاکتور.',
  tiered:
    'درصد با بزرگ‌تر شدن فاکتور بالا می‌رود. هر پله فقط روی بخشی از مبلغ فاکتور که در آن پله است اعمال می‌شود.',
};

interface TierDraft {
  from: string;
  percent: string;
}

interface Draft {
  method: CommissionMethod;
  percent: string;
  fixed: string;
  tiers: TierDraft[];
  minBase: string;
  cap: string;
  release: ReleasePolicy;
  target: string;
  effectiveFrom: string;
  active: boolean;
  note: string;
}

const PERCENT = /^\d{1,3}(\.\d{1,2})?$/;

function draftFrom(plan: CommissionPlan | null, unit: 'rial' | 'toman'): Draft {
  return {
    method: plan?.method ?? 'percent_of_sale',
    percent: plan?.ratePercent ?? '',
    fixed: rialToDisplay(plan?.fixedRial ?? null, unit),
    tiers: plan?.tiers?.map((t) => ({
      from: rialToDisplay(t.fromRial, unit),
      percent: t.ratePercent,
    })) ?? [
      { from: '0', percent: '' },
      { from: '', percent: '' },
    ],
    minBase: rialToDisplay(plan && plan.minBaseRial !== '0' ? plan.minBaseRial : null, unit),
    cap: rialToDisplay(plan?.capRial ?? null, unit),
    release: plan?.releasePolicy ?? 'on_collection',
    target: rialToDisplay(plan?.monthlyTargetRial ?? null, unit),
    effectiveFrom: plan?.effectiveFrom ?? '',
    active: plan ? plan.status === 'active' : true,
    note: plan?.note ?? '',
  };
}

function body(draft: Draft, unit: 'rial' | 'toman'): Record<string, unknown> {
  const rial = (v: string) =>
    v.trim() === '' ? undefined : displayToRial(toEnglishDigits(v), unit);
  return {
    method: draft.method,
    ...(draft.method === 'percent_of_sale'
      ? { ratePercent: toEnglishDigits(draft.percent).trim() }
      : {}),
    ...(draft.method === 'fixed_per_invoice' ? { fixedRial: rial(draft.fixed) } : {}),
    ...(draft.method === 'tiered'
      ? {
          tiers: draft.tiers.map((t, index) => ({
            fromRial: index === 0 ? '0' : rial(t.from),
            ratePercent: toEnglishDigits(t.percent).trim(),
          })),
        }
      : {}),
    minBaseRial: rial(draft.minBase),
    capRial: rial(draft.cap),
    releasePolicy: draft.release,
    monthlyTargetRial: rial(draft.target),
    effectiveFrom: draft.effectiveFrom || undefined,
    status: draft.active ? 'active' : 'inactive',
    note: draft.note.trim() || undefined,
  };
}

function problems(draft: Draft): Record<string, string> {
  const out: Record<string, string> = {};
  if (draft.method === 'percent_of_sale') {
    const v = toEnglishDigits(draft.percent).trim();
    if (!PERCENT.test(v) || Number(v) > 100)
      out['percent'] = 'درصد باید بین ۰ تا ۱۰۰ و حداکثر با دو رقم اعشار باشد.';
  }
  if (draft.method === 'fixed_per_invoice' && !draft.fixed.trim())
    out['fixed'] = 'مبلغ را وارد کنید.';
  if (draft.method === 'tiered') {
    let last = -1n;
    draft.tiers.forEach((tier, index) => {
      const percent = toEnglishDigits(tier.percent).trim();
      if (!PERCENT.test(percent) || Number(percent) > 100)
        out[`tier${index}p`] = 'درصد معتبر نیست.';
      if (index > 0) {
        const from = toEnglishDigits(tier.from).replace(/\D/g, '');
        if (!from) out[`tier${index}f`] = 'شروع پله را وارد کنید.';
        else if (BigInt(from) <= last)
          out[`tier${index}f`] = 'شروع هر پله باید از پلهٔ قبل بیشتر باشد.';
        else last = BigInt(from);
      }
    });
  }
  return out;
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.envelope.error.details?.[0]?.message ?? error.message;
  return 'ذخیره انجام نشد؛ دوباره تلاش کنید.';
}

function Money({
  value,
  onChange,
  label,
  unitLabel,
  error,
  hint,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label: string;
  readonly unitLabel: string;
  readonly error?: string | undefined;
  readonly hint?: string;
}) {
  const digits = toEnglishDigits(value).replace(/\D/g, '');
  return (
    <V2FormField label={`${label} (${unitLabel})`} width="medium" {...(error ? { error } : {})}>
      <input
        className="sk-input sk-num"
        inputMode="numeric"
        dir="ltr"
        value={digits ? faDigits(BigInt(digits).toLocaleString('en-US')) : ''}
        onChange={(e) => onChange(toEnglishDigits(e.target.value).replace(/\D/g, ''))}
      />
      {hint ? <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{hint}</span> : null}
    </V2FormField>
  );
}

/**
 * «مدیریت پورسانت» of one salesperson: how this person is paid — a share of
 * the whole sale, a fixed amount per invoice, bracketed rates, or the
 * per-product rates — plus a minimum invoice, a cap, when it is released, a
 * monthly target, and a live example of what the plan would pay.
 *
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html — V2Modal + V2FormGrid
 *   language (no dedicated frame). status: KIT_CONFORMANT, V2-only.
 */
export function CommissionPlanModal({
  base,
  member,
  plan,
  accountsConfigured,
  canManage,
  period,
  onClose,
  onSaved,
}: {
  readonly base: string;
  readonly member: TeamMember;
  readonly plan: CommissionPlan | null;
  readonly accountsConfigured: boolean;
  readonly canManage: boolean;
  readonly period: PeriodMetrics;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { unit, label: unitLabel } = useAmountDisplay();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(plan, unit));
  const [example, setExample] = useState(rialToDisplay('100000000', unit));
  const [preview, setPreview] = useState<{
    commissionRial: string;
    effectivePercent: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => setDraft(draftFrom(plan, unit)), [plan, unit]);

  const issues = useMemo(() => problems(draft), [draft]);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Live example — the server is the single source of the arithmetic.
  const previewKey = JSON.stringify([body(draft, unit), example, Object.keys(issues).length]);
  useEffect(() => {
    if (draft.method === 'product_rate' || Object.keys(issues).length > 0 || !example) {
      setPreview(null);
      return;
    }
    const handle = window.setTimeout(() => {
      apiPost<{ commissionRial: string; effectivePercent: string }>(
        `${base}/api/v1/sales/commission/plans-preview`,
        { plan: body(draft, unit), baseRial: displayToRial(example, unit) },
      )
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => window.clearTimeout(handle);
  }, [previewKey]);

  async function save() {
    setShowErrors(true);
    if (Object.keys(issues).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      await apiPut(
        `${base}/api/v1/sales/commission/plans/${member.salespersonId}`,
        body(draft, unit),
      );
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !window.confirm(
        'طرح اختصاصی حذف شود؟ این فروشنده دوباره با نرخ هر محصول پورسانت می‌گیرد. پورسانت فاکتورهای قبلی تغییر نمی‌کند.',
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await apiDelete(`${base}/api/v1/sales/commission/plans/${member.salespersonId}`);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const disabled = !canManage || busy;
  const methodCard = (method: CommissionMethod) => (
    <label key={method} className={`team-method${draft.method === method ? ' is-on' : ''}`}>
      <input
        type="radio"
        name="commission-method"
        checked={draft.method === method}
        disabled={disabled}
        onChange={() => set('method', method)}
      />
      <span>
        <b>{METHOD_LABELS[method]}</b>
        <small>{METHOD_HINTS[method]}</small>
      </span>
    </label>
  );

  return (
    <V2Modal
      open
      size="lg"
      title={`مدیریت پورسانت — ${member.name}`}
      onClose={onClose}
      dismissible={!busy}
      footer={
        <>
          {canManage ? (
            <V2Button variant="primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'در حال ذخیره…' : 'ذخیرهٔ طرح پورسانت'}
            </V2Button>
          ) : null}
          {canManage && plan ? (
            <V2Button variant="ghost" disabled={busy} onClick={() => void remove()}>
              حذف طرح اختصاصی
            </V2Button>
          ) : null}
          <V2Button variant="ghost" onClick={onClose} disabled={busy}>
            بستن
          </V2Button>
        </>
      }
    >
      <div className="team-plan">
        <div className="team-plan__stats">
          <span>
            فروش دوره <b className="sk-num">{formatRialFa(period.salesRial, unit)}</b> {unitLabel}
          </span>
          <span>
            پورسانت دوره <b className="sk-num">{formatRialFa(period.commissionRial, unit)}</b>{' '}
            {unitLabel}
          </span>
          <span>
            فاکتور قطعی <b className="sk-num">{faDigits(period.invoiceCount)}</b>
          </span>
          {plan ? (
            <V2Badge tone={plan.status === 'active' ? 'success' : 'neutral'}>
              {plan.status === 'active' ? 'طرح اختصاصی فعال' : 'طرح غیرفعال'}
            </V2Badge>
          ) : (
            <V2Badge tone="neutral">طرح اختصاصی ندارد — نرخ هر محصول</V2Badge>
          )}
        </div>

        {!canManage ? (
          <V2Alert tone="info">تغییر طرح پورسانت با مجوز «مدیریت قواعد پورسانت» است.</V2Alert>
        ) : null}
        {!accountsConfigured ? (
          <V2Alert tone="warn">
            حساب‌های سند پورسانت هنوز در مالی تنظیم نشده و تا آن زمان طرح فعال ذخیره نمی‌شود.{' '}
            <Link to={`${base}/finance/sales-accounts`}>تنظیم در مالی</Link>
          </V2Alert>
        ) : null}
        {error ? <V2Alert tone="danger">{error}</V2Alert> : null}

        <section aria-label="روش محاسبه">
          <h3 className="team-plan__h">این فروشنده چطور پورسانت می‌گیرد؟</h3>
          <div className="team-methods">
            {(Object.keys(METHOD_LABELS) as CommissionMethod[]).map(methodCard)}
          </div>

          {draft.method === 'percent_of_sale' ? (
            <V2FormGrid>
              <V2FormField
                label="درصد از کل فروش (٪)"
                width="short"
                {...(showErrors && issues['percent'] ? { error: issues['percent'] } : {})}
              >
                <input
                  className="sk-input sk-num"
                  dir="ltr"
                  inputMode="decimal"
                  disabled={disabled}
                  value={faDigits(draft.percent)}
                  onChange={(e) => set('percent', toEnglishDigits(e.target.value))}
                />
              </V2FormField>
            </V2FormGrid>
          ) : null}
          {draft.method === 'fixed_per_invoice' ? (
            <V2FormGrid>
              <Money
                label="مبلغ ثابت برای هر فاکتور"
                unitLabel={unitLabel}
                value={draft.fixed}
                onChange={(v) => set('fixed', v)}
                error={showErrors ? issues['fixed'] : undefined}
              />
            </V2FormGrid>
          ) : null}
          {draft.method === 'tiered' ? (
            <div className="team-tiers">
              {draft.tiers.map((tier, index) => (
                <div key={index} className="team-tiers__row">
                  <span className="team-tiers__label">
                    {index === 0 ? 'از اولین ریال فاکتور' : 'از مبلغ فاکتور'}
                  </span>
                  {index === 0 ? (
                    <b className="sk-num">۰</b>
                  ) : (
                    <input
                      className="sk-input sk-num"
                      dir="ltr"
                      inputMode="numeric"
                      aria-label={`شروع پلهٔ ${index + 1} (${unitLabel})`}
                      disabled={disabled}
                      value={
                        tier.from
                          ? faDigits(
                              BigInt(
                                toEnglishDigits(tier.from).replace(/\D/g, '') || '0',
                              ).toLocaleString('en-US'),
                            )
                          : ''
                      }
                      onChange={(e) =>
                        set(
                          'tiers',
                          draft.tiers.map((t, i) =>
                            i === index
                              ? { ...t, from: toEnglishDigits(e.target.value).replace(/\D/g, '') }
                              : t,
                          ),
                        )
                      }
                    />
                  )}
                  <span className="team-tiers__label">{unitLabel} به بالا</span>
                  <input
                    className="sk-input sk-num"
                    dir="ltr"
                    inputMode="decimal"
                    style={{ width: 80 }}
                    aria-label={`درصد پلهٔ ${index + 1}`}
                    disabled={disabled}
                    value={faDigits(tier.percent)}
                    onChange={(e) =>
                      set(
                        'tiers',
                        draft.tiers.map((t, i) =>
                          i === index ? { ...t, percent: toEnglishDigits(e.target.value) } : t,
                        ),
                      )
                    }
                  />
                  <span>٪</span>
                  {index > 1 && canManage ? (
                    <V2Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        set(
                          'tiers',
                          draft.tiers.filter((_, i) => i !== index),
                        )
                      }
                    >
                      حذف
                    </V2Button>
                  ) : null}
                  {showErrors && (issues[`tier${index}f`] || issues[`tier${index}p`]) ? (
                    <span role="alert" className="team-tiers__err">
                      {issues[`tier${index}f`] ?? issues[`tier${index}p`]}
                    </span>
                  ) : null}
                </div>
              ))}
              {canManage && draft.tiers.length < 10 ? (
                <V2Button
                  size="sm"
                  variant="secondary"
                  onClick={() => set('tiers', [...draft.tiers, { from: '', percent: '' }])}
                >
                  افزودن پله
                </V2Button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section aria-label="شرط‌ها و سقف">
          <h3 className="team-plan__h">شرط‌ها و سقف (برای هر فاکتور)</h3>
          <V2FormGrid>
            <Money
              label="حداقل مبلغ فاکتور برای پورسانت"
              unitLabel={unitLabel}
              value={draft.minBase}
              onChange={(v) => set('minBase', v)}
              hint="فاکتورهای کوچک‌تر از این مبلغ پورسانت ندارند. خالی = بدون حداقل."
            />
            <Money
              label="سقف پورسانت هر فاکتور"
              unitLabel={unitLabel}
              value={draft.cap}
              onChange={(v) => set('cap', v)}
              hint="خالی = بدون سقف."
            />
          </V2FormGrid>
        </section>

        <section aria-label="زمان پرداخت">
          <h3 className="team-plan__h">پورسانت کی آزاد می‌شود؟</h3>
          <div className="team-methods team-methods--two">
            {(Object.keys(RELEASE_LABELS) as ReleasePolicy[]).map((policy) => (
              <label
                key={policy}
                className={`team-method${draft.release === policy ? ' is-on' : ''}`}
              >
                <input
                  type="radio"
                  name="commission-release"
                  checked={draft.release === policy}
                  disabled={disabled}
                  onChange={() => set('release', policy)}
                />
                <span>
                  <b>{RELEASE_LABELS[policy]}</b>
                  <small>
                    {policy === 'on_collection'
                      ? 'به نسبت مبلغ وصول‌شده از مشتری آزاد می‌شود؛ ریسک وصول‌نشدن با شرکت نیست.'
                      : 'کل پورسانت بدون انتظار برای وصول قابل پرداخت است.'}
                  </small>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section aria-label="هدف و اعتبار">
          <h3 className="team-plan__h">هدف و اعتبار</h3>
          <V2FormGrid>
            <Money
              label="هدف فروش ماهانه"
              unitLabel={unitLabel}
              value={draft.target}
              onChange={(v) => set('target', v)}
              hint="در صفحهٔ عملکرد تیم، پیشرفت نسبت به این هدف نمایش داده می‌شود."
            />
            <V2FormField label="اعتبار طرح از تاریخ" width="medium">
              <JalaliDateInput
                value={draft.effectiveFrom}
                onChange={(v) => set('effectiveFrom', v)}
              />
            </V2FormField>
            <V2FormField label="وضعیت طرح" width="short">
              <select
                className="sk-select"
                disabled={disabled}
                value={draft.active ? 'active' : 'inactive'}
                onChange={(e) => set('active', e.target.value === 'active')}
              >
                <option value="active">فعال</option>
                <option value="inactive">غیرفعال</option>
              </select>
            </V2FormField>
            <V2FormField label="یادداشت (اختیاری)" width="wide">
              <input
                className="sk-input"
                maxLength={500}
                disabled={disabled}
                value={draft.note}
                onChange={(e) => set('note', e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>
        </section>

        <section aria-label="نمونه محاسبه" className="team-example">
          <h3 className="team-plan__h">نمونه محاسبه</h3>
          {draft.method === 'product_rate' ? (
            <p className="team-example__text">
              در این روش پورسانت از روی کالاهای هر فاکتور محاسبه می‌شود؛ نمونه عددی ندارد.
            </p>
          ) : (
            <div className="team-example__row">
              <Money
                label="اگر فاکتوری به مبلغ"
                unitLabel={unitLabel}
                value={example}
                onChange={setExample}
              />
              <p className="team-example__text" aria-live="polite">
                {preview ? (
                  <>
                    پورسانت: <b className="sk-num">{formatRialFa(preview.commissionRial, unit)}</b>{' '}
                    {unitLabel} <small>(معادل {faDigits(preview.effectivePercent)}٪ فاکتور)</small>
                  </>
                ) : (
                  'مقادیر طرح را کامل کنید تا نمونه نمایش داده شود.'
                )}
              </p>
            </div>
          )}
        </section>
      </div>
    </V2Modal>
  );
}
